import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowRight, Plus, Search, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { InvoiceStatusBadge } from '@/components/shared/StatusBadges'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PaginatedTabTable } from '@/components/shared/PaginatedTabTable'
import { InvoiceSendDialog } from '@/components/billing/InvoiceSendDialog'
import {
  ClientTabSection,
  clientTabFilterOptionClass,
  clientTabActionBtnClass,
  clientTabActionsCellClass,
  clientTabActionsColClass,
  clientTabFirstTdClass,
  clientTabFilterTriggerClass,
  clientTabPrimaryBtnClass,
  clientTabSearchInputClass,
  clientTabViewDetailsBtnClass,
} from '@/components/clients/tabs/ClientTabSection'
import { useClientInvoices } from '@/hooks/useClients'
import { invalidatePortalMetricQueries } from '@/hooks/usePortalMetrics'
import { useListParams } from '@/hooks/useListParams'
import { invoicesApi } from '@/services/invoices'
import { formatCurrency, formatDate } from '@/lib/format'
import { primaryEmail } from '@/lib/clientContacts'
import type { Invoice } from '@/types'

const ALL = 'all'

// 2.4.12 Client details — invoices tab.
export function InvoicesTab({
  clientId,
  clientEmail,
  canCreateInvoice = false,
}: {
  clientId: string
  clientEmail: string
  canCreateInvoice?: boolean
}) {
  const { t } = useTranslation(['clients', 'invoices', 'common'])
  const navigate = useNavigate()
  const qc = useQueryClient()
  const {
    page,
    setPage,
    limit,
    setLimit,
    search,
    onSearchChange,
    filters,
    setFilters,
    query,
  } = useListParams<{ status?: string }>({})
  const { data, isLoading, isError } = useClientInvoices(clientId, query)

  const [sendTarget, setSendTarget] = useState<Invoice | null>(null)
  const openInvoice = (invoiceId: string) => navigate(`/invoices/${invoiceId}`)

  return (
    <ClientTabSection
      title={t('detail.tabs.invoices')}
      toolbar={
        <div className='flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'>
          <div className='flex min-w-0 flex-wrap items-center gap-4'>
            <Select
              value={filters.status ?? ALL}
              onValueChange={v =>
                setFilters({ status: v === ALL ? undefined : v })
              }
            >
              <SelectTrigger className={clientTabFilterTriggerClass}>
                <SelectValue placeholder={t('tabFilters.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL} className={clientTabFilterOptionClass}>
                  {t('tabFilters.allStatuses')}
                </SelectItem>
                <SelectItem value='pro_forma'>
                  {t('invoices.filters.proForma')}
                </SelectItem>
                <SelectItem value='pro_forma_sent'>
                  {t('invoices.filters.proFormaSent')}
                </SelectItem>
                <SelectItem value='validated'>
                  {t('invoices.filters.validated')}
                </SelectItem>
                <SelectItem value='pending'>
                  {t('invoices.filters.pending')}
                </SelectItem>
                <SelectItem value='paid' className={clientTabFilterOptionClass}>
                  {t('invoices.filters.paid')}
                </SelectItem>
                <SelectItem
                  value='partially_paid'
                  className={clientTabFilterOptionClass}
                >
                  {t('invoices.filters.partiallyPaid')}
                </SelectItem>
                <SelectItem
                  value='overdue'
                  className={clientTabFilterOptionClass}
                >
                  {t('invoices.filters.overdue')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className='flex flex-col gap-4 sm:flex-row sm:items-center'>
            <div className='relative min-w-0 flex-1'>
              <Search className='pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#3d434e]' />
              <Input
                value={search}
                onChange={e => onSearchChange(e.target.value)}
                placeholder={t('invoices.searchPlaceholder')}
                maxLength={50}
                className={clientTabSearchInputClass}
              />
            </div>
            {canCreateInvoice && (
              <Button
                className={clientTabPrimaryBtnClass}
                onClick={() => navigate(`/invoices/new?client_id=${clientId}`)}
              >
                <Plus className='size-4' /> {t('invoices.addNew')}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <PaginatedTabTable<Invoice>
        clientHistoryStyle
        data={data}
        isLoading={isLoading}
        isError={isError}
        emptyMessage={t('invoices.empty')}
        errorMessage={t('invoices.error')}
        page={page}
        pageSize={limit}
        onPageChange={setPage}
        onPageSizeChange={setLimit}
        rowKey={i => i.id}
        onRowClick={i => openInvoice(i.id)}
        columns={[
          {
            header: t('invoices.columns.number'),
            cellClassName: clientTabFirstTdClass,
            cell: i => (
              <div className='text-sm leading-[1.4]'>
                {i.number ?? i.id.slice(0, 8)}
              </div>
            ),
          },
          {
            header: t('invoices.columns.status'),
            className: 'text-center',
            cell: i => (
              <InvoiceStatusBadge
                status={i.status}
                dueDate={i.due_date}
                className='capitalize'
              />
            ),
          },
          {
            header: t('invoices.columns.amount'),
            className: 'text-center',
            cell: i => (
              <span className='text-sm font-medium leading-[1.4] text-[#171a20]'>
                {formatCurrency(i.total, 'EUR', 2)}
              </span>
            ),
          },
          {
            header: t('invoices.columns.issueDate'),
            cell: i => (
              <span className='text-sm font-normal leading-[1.4] text-[#171a20]'>
                {formatDate(i.issue_date)}
              </span>
            ),
          },
          {
            header: t('invoices.columns.dueDate'),
            cell: i => (
              <span className='text-sm font-normal leading-[1.4] text-[#171a20]'>
                {formatDate(i.due_date)}
              </span>
            ),
          },
          {
            header: t('list.columns.actions'),
            className: clientTabActionsColClass,
            cell: i => (
              <div
                className={clientTabActionsCellClass}
                onClick={e => e.stopPropagation()}
              >
                {i.status === 'validated' && (
                  <button
                    type='button'
                    className={clientTabActionBtnClass}
                    title={t('invoices.actions.send')}
                    onClick={() => setSendTarget(i)}
                  >
                    <Send className='h-4 w-4' />
                  </button>
                )}
                <button
                  type='button'
                  onClick={() => openInvoice(i.id)}
                  className={clientTabViewDetailsBtnClass}
                >
                  {t('common:actions.view')}
                  <ArrowRight className='h-4 w-4 shrink-0' />
                </button>
              </div>
            ),
          },
        ]}
      />

      {sendTarget && (
        <InvoiceSendDialog
          open
          onOpenChange={o => !o && setSendTarget(null)}
          title={t('invoices:detail.sendTitle')}
          defaultTo={
            primaryEmail(sendTarget.client) ??
            sendTarget.client?.email ??
            clientEmail
          }
          defaultSubject={t('invoices:detail.sendSubject', {
            number: sendTarget.number ?? '',
          }).trim()}
          clientName={
            sendTarget.client?.name ??
            sendTarget.client_name ??
            t('invoices:detail.invoiceSendDialog.unknownClient')
          }
          reference={sendTarget.number ?? sendTarget.id.slice(0, 8)}
          totalLabel={formatCurrency(sendTarget.total, 'EUR', 2)}
          successMessage={t('invoices:detail.sendSuccess')}
          errorMessage={t('invoices:detail.sendError')}
          onSend={async body => {
            await invoicesApi.send(sendTarget.id, {
              recipient_email: body.recipient_email,
              message: body.message || undefined,
              include_bank_details: body.include_bank_details,
            })
            qc.invalidateQueries({
              queryKey: ['clients', clientId, 'invoices'],
            })
            qc.invalidateQueries({ queryKey: ['invoices', 'list'] })
            qc.invalidateQueries({ queryKey: ['invoices', 'summary'] })
            invalidatePortalMetricQueries(qc)
          }}
        />
      )}
    </ClientTabSection>
  )
}
